// API Routing Configuration for BigSystemOne.
// Every microservice is reached through the shared AWS API Gateway. Direct
// Lambda URLs are no longer enumerated here so they cannot leak into the JS
// bundle or be used to bypass Gateway auth/throttling.

const GATEWAY_URL =
  process.env.REACT_APP_API_GATEWAY_URL ||
  'https://diuh85h4d5.execute-api.us-east-1.amazonaws.com/prod';

export interface ApiEndpoint {
  url: string;
  name: string;
  status: 'active' | 'testing' | 'inactive';
}

export interface ApiConfiguration {
  gateway: {
    baseUrl: string;
    stage: string;
    enabled: boolean;
  };
  migration: {
    mode: 'legacy' | 'gateway' | 'dual';
    trafficPercentage: number; // Percentage of traffic to send to API Gateway
    rolloutPhase: 'planning' | 'testing' | 'partial' | 'complete';
  };
  microservices: {
    aiImages: ApiEndpoint;
    menuManagement: ApiEndpoint;
    taxManagement: ApiEndpoint;
    coreData: ApiEndpoint;
    stores: ApiEndpoint;
    dayparts: ApiEndpoint;
    employees: ApiEndpoint;
    socialMedia: ApiEndpoint;
    discounts: ApiEndpoint;
    securityAudit: ApiEndpoint;
    contactForm: ApiEndpoint;
    ubiquitiNetwork: ApiEndpoint;
    messaging: ApiEndpoint;
    categories: ApiEndpoint;
    menuBoards: ApiEndpoint;
    scheduling: ApiEndpoint;
    posSync: ApiEndpoint;
    tableCharts: ApiEndpoint;
    weather: ApiEndpoint;
    waitlist: ApiEndpoint;
    payment: ApiEndpoint;
    aiChat: ApiEndpoint;
    voiceOrdering: ApiEndpoint;
    voiceConfig: ApiEndpoint;
    phoneAssistant: ApiEndpoint;
    equipmentMonitoring: ApiEndpoint;
    posIntegration: ApiEndpoint;
    reviewManagement: ApiEndpoint;
    reviewTrigger: ApiEndpoint;
    supportChat: ApiEndpoint;
    billing: ApiEndpoint;
    stripeWebhooks: ApiEndpoint;
    commandCenter: ApiEndpoint;
    suggestionEngine: ApiEndpoint;
    askBso: ApiEndpoint;
    cloverAdapter: ApiEndpoint;
    squareAdapter: ApiEndpoint;
    tokenRefresh: ApiEndpoint;
    // Intelligent Inventory microservices (API Gateway only - new)
    inventoryCore: ApiEndpoint;
    recipes: ApiEndpoint;
    inventoryForecast: ApiEndpoint;
    inventoryOrders: ApiEndpoint;
    inventoryReceiving: ApiEndpoint;
    inventoryInvoices: ApiEndpoint;
    inventoryVision: ApiEndpoint;
    suppliers: ApiEndpoint;
    inventoryNetwork: ApiEndpoint;
    inventoryWaste: ApiEndpoint;
    uploads: ApiEndpoint;
    pricing: ApiEndpoint;
    modifiers: ApiEndpoint;
    approvals: ApiEndpoint;
  };
}

// All microservice entries share the same API Gateway base URL. We keep the
// per-service structure so components that inspect `API_CONFIG.microservices[x]`
// continue to work, but routing is now gateway-only.
const svc = (name: string): ApiEndpoint => ({
  url: GATEWAY_URL,
  name,
  status: 'active'
});

export const API_CONFIG: ApiConfiguration = {
  gateway: {
    baseUrl: GATEWAY_URL,
    stage: '',
    enabled: true
  },
  migration: {
    mode: 'gateway',
    trafficPercentage: 100,
    rolloutPhase: 'complete'
  },
  microservices: {
    aiImages: svc('AI Images API'),
    menuManagement: svc('Menu Management API'),
    taxManagement: svc('Tax Management API'),
    coreData: svc('Core Data API'),
    stores: svc('Stores API'),
    dayparts: svc('Dayparts API'),
    employees: svc('Employees API'),
    socialMedia: svc('Social Media API'),
    discounts: svc('Discounts API'),
    securityAudit: svc('Security Audit API'),
    contactForm: svc('Contact Form API'),
    ubiquitiNetwork: svc('Ubiquiti Network API'),
    messaging: svc('Messaging & Alerts API'),
    categories: svc('Categories API'),
    menuBoards: svc('Menu Boards API'),
    scheduling: svc('Scheduling API'),
    posSync: svc('POS Sync API'),
    tableCharts: svc('Table Charts API'),
    weather: svc('Weather Analytics API'),
    waitlist: svc('Waitlist API'),
    payment: svc('Payment API'),
    aiChat: svc('AI Chat API'),
    voiceOrdering: svc('Voice Ordering API'),
    voiceConfig: svc('Voice Config API'),
    phoneAssistant: svc('Phone Assistant API'),
    equipmentMonitoring: svc('Equipment Monitoring API'),
    posIntegration: svc('POS Integration API'),
    reviewManagement: svc('Review Management API'),
    reviewTrigger: svc('Review Trigger API'),
    supportChat: svc('Support Chat API'),
    billing: svc('Billing API'),
    stripeWebhooks: svc('Stripe Webhooks API'),
    commandCenter: svc('Command Center API'),
    suggestionEngine: svc('Suggestion Engine API'),
    askBso: svc('Ask BSO API'),
    cloverAdapter: svc('Clover POS Adapter'),
    squareAdapter: svc('Square POS Adapter'),
    tokenRefresh: svc('Token Refresh Service'),
    inventoryCore: svc('Inventory Core API'),
    recipes: svc('Recipes API'),
    inventoryForecast: svc('Inventory Demand Forecast API'),
    inventoryOrders: svc('Inventory Purchase Orders API'),
    inventoryReceiving: svc('Inventory Receiving API'),
    inventoryInvoices: svc('Inventory Invoices API'),
    inventoryVision: svc('Inventory Vision API'),
    suppliers: svc('Suppliers API'),
    inventoryNetwork: svc('Inventory Network Alerts API'),
    inventoryWaste: svc('Inventory Waste API'),
    uploads: svc('Uploads API'),
    pricing: svc('Hierarchical Pricing API'),
    modifiers: svc('Universal Modifiers API'),
    approvals: svc('Approval Workflow API')
  }
};

// Microservices status - PURE MICROSERVICES ARCHITECTURE  
export const MICROSERVICES_ENABLED = {
  aiImages: true,         // ACTIVE: Using dedicated AI Images microservice
  menuManagement: true,   // ACTIVE: Menu Items microservice is working
  taxManagement: true,    // ACTIVE: Using comprehensive API temporarily
  coreData: true,         // ACTIVE: Using working Enterprise API
  stores: true,           // ACTIVE: Using working stores microservice
  dayparts: true,         // ACTIVE: Dayparts microservice is working
  employees: true,        // ACTIVE: Enhanced employee management microservice
  socialMedia: true,      // ACTIVE: Social media microservice deployed and active
  discounts: true,        // ACTIVE: Discount management microservice deployed and active
  securityAudit: true,    // ACTIVE: Security audit and compliance system
  contactForm: true,      // ACTIVE: Contact form submission handler
  ubiquitiNetwork: true,  // ACTIVE: UniFi network management and video system
  messaging: true,        // ACTIVE: Restaurant messaging and alert system deployed
  categories: true,       // ACTIVE: Categories management microservice
  menuBoards: true,       // ACTIVE: Digital menu boards microservice deployed and active
  scheduling: true,       // ACTIVE: Employee scheduling and time tracking microservice
  posSync: true,          // ACTIVE: POS data synchronization microservice
  tableCharts: true,      // ACTIVE: Table chart management and layout configuration
  weather: true,          // ACTIVE: Weather analytics with AccuWeather integration
  waitlist: true,         // ACTIVE: Restaurant waitlist and host management
  payment: true,          // ACTIVE: Payment processing and checkout microservice
  aiChat: true,           // ACTIVE: AI chat assistant for natural language operations
  voiceOrdering: true,    // ACTIVE: Voice ordering session management for Jetson/POS/Drive-thru
  voiceConfig: true,      // ACTIVE: Voice ordering configuration and device management
  phoneAssistant: true,   // ACTIVE: AWS Connect/Lex phone ordering, reservations, waitlist
  equipmentMonitoring: true,  // ACTIVE: Equipment monitoring and reporting for restaurant hardware
  posIntegration: true,       // ACTIVE: POS integration (Toast adapter, order polling)
  reviewManagement: true,     // ACTIVE: Review management (Google Reviews, AI responses)
  reviewTrigger: true,        // ACTIVE: Review trigger (request scheduling, micro-survey)
  supportChat: true,          // ACTIVE: AI-powered support chat with conversation persistence
  billing: true,             // ACTIVE: Stripe billing, checkout, promo code management
  stripeWebhooks: true,      // ACTIVE: Stripe webhook event processing
  commandCenter: true,       // ACTIVE: Command Center analytics dashboard API
  suggestionEngine: true,    // ACTIVE: AI suggestion engine for operational insights
  askBso: true,              // ACTIVE: Conversational analytics (Ask BSO)
  // Intelligent Inventory microservices
  inventoryCore: true,       // ACTIVE: Inventory core (stock, counts, snapshots, waste, ingredients)
  recipes: true,             // ACTIVE: Recipe CRUD + explode
  inventoryForecast: true,   // ACTIVE: 14-day demand forecasting
  inventoryOrders: true,     // ACTIVE: Auto-generated purchase orders
  inventoryReceiving: true,  // ACTIVE: Delivery receipts and three-way matching
  inventoryInvoices: true,   // ACTIVE: Textract invoice scanning
  inventoryVision: true,     // ACTIVE: Computer-vision counts and receiving
  suppliers: true,           // ACTIVE: Supplier CRUD + performance scoring
  inventoryNetwork: true,    // ACTIVE: Network-wide inventory alerts
  inventoryWaste: true,      // ACTIVE: Waste analytics
  uploads: true,             // ACTIVE: Shared image/document upload handler
  pricing: true,             // ACTIVE: Hierarchical pricing overrides (enterprise/concept/franchisee/store)
  modifiers: true,           // ACTIVE: Universal Modifier Architecture (groups, options, templates, rules engine)
  approvals: true            // ACTIVE: Approval workflow (voids, discounts, refunds, comps, price overrides)
};

/**
 * Resolve the URL (base URL + optional service path prefix) for a microservice.
 * Always routes through the shared API Gateway.
 */
export const getApiUrl = (
  apiType: keyof typeof MICROSERVICES_ENABLED,
  _options: { forceMode?: 'legacy' | 'gateway' } = {}
): string => {
  if (!MICROSERVICES_ENABLED[apiType]) {
    throw new Error(`Microservice ${apiType} is disabled`);
  }
  return getGatewayUrl(apiType) || GATEWAY_URL;
};

// Specific endpoint builders
export const API_ENDPOINTS = {
  // AI Image Generation
  generateImage: () => `${getApiUrl('aiImages')}/api/menu-items/generate-image`,
  
  // AI Description Generation - Using Menu Management API (needs to be implemented)
  generateDescription: () => `${getApiUrl('menuManagement')}/api/menu-items/complete-description`,
  
  // Menu Management
  menuItems: (enterpriseId?: string) => 
    enterpriseId 
      ? `${getApiUrl('menuManagement')}/api/menu-items?enterpriseId=${enterpriseId}`
      : `${getApiUrl('menuManagement')}/api/menu-items`,
  
  menuItem: (menuItemId: string) => 
    `${getApiUrl('menuManagement')}/api/menu-items/${menuItemId}`,
  
  // Menu Ingestion - Using Menu Management API
  // All four legacy /api/menu-setup/* builders now target the real
  // lambda-menu-management ingestion pipeline (Textract + Bedrock).
  menuSetupUploadDocument: () => `${getApiUrl('menuManagement')}/api/menu-management/ingestion`,
  menuSetupScrapeWebsite: () => `${getApiUrl('menuManagement')}/api/menu-management/ingestion`,
  menuSetupJob: (jobId: string) => `${getApiUrl('menuManagement')}/api/menu-management/ingestion/job/${jobId}`,
  menuSetupJobItems: (jobId: string) => `${getApiUrl('menuManagement')}/api/menu-management/ingestion/job/${jobId}/items`,
  menuSetupApproveJob: (jobId: string) => `${getApiUrl('menuManagement')}/api/menu-management/ingestion/job/${jobId}/approve`,
  
  // File Uploads — shared lambda-uploads microservice
  uploadMenu: (entityId: string) => `${getApiUrl('uploads')}/api/uploads/menu/${entityId}`,
  uploadEmployee: (entityId?: string) => entityId ? `${getApiUrl('uploads')}/api/uploads/employee/${entityId}` : `${getApiUrl('uploads')}/api/uploads/employee`,
  uploadSupplier: (entityId: string) => `${getApiUrl('uploads')}/api/uploads/supplier/${entityId}`,
  uploadMarketing: () => `${getApiUrl('uploads')}/api/uploads/marketing`,
  uploadGeneral: (entityId?: string) => entityId ? `${getApiUrl('uploads')}/api/uploads/general/${entityId}` : `${getApiUrl('uploads')}/api/uploads/general`,
  
  // Tax Management (dedicated microservice)
  taxEnterprise: (enterpriseId: string) => 
    `${getApiUrl('taxManagement')}/enterprise/${enterpriseId}`,
  
  taxConfig: (entityId?: string, configType?: string) => 
    entityId && configType
      ? `${getApiUrl('taxManagement')}/config/${entityId}/${configType}`
      : `${getApiUrl('taxManagement')}/config`,
  
  taxCalculate: () => `${getApiUrl('taxManagement')}/calculate`,
  
  taxBulkUpdate: () => `${getApiUrl('taxManagement')}/bulk-update`,
  
  // Core Data (Enterprise, Concept data)
  enterprises: () => `${getApiUrl('coreData')}/api/enterprises`,
  enterprise: (enterpriseId: string) => `${getApiUrl('coreData')}/api/enterprises/${enterpriseId}`,
  concepts: (enterpriseId: string) => 
    `${getApiUrl('coreData')}/api/enterprises/${enterpriseId}/concepts`,
  franchisees: (enterpriseId: string) => 
    `${getApiUrl('coreData')}/api/enterprises/${enterpriseId}/franchisees`,
  
  // Global admin endpoints (for hierarchy management)
  allConcepts: () => `${getApiUrl('coreData')}/api/concepts`,
  allFranchisees: () => `${getApiUrl('coreData')}/api/franchisees`,
  conceptCrud: (conceptId?: string) => conceptId 
    ? `${getApiUrl('coreData')}/api/concepts/${conceptId}`
    : `${getApiUrl('coreData')}/api/concepts`,
  franchiseeCrud: (franchiseeId?: string) => franchiseeId 
    ? `${getApiUrl('coreData')}/api/franchisees/${franchiseeId}`
    : `${getApiUrl('coreData')}/api/franchisees`,
  initializeSampleData: () => `${getApiUrl('coreData')}/api/initialize-sample-data`,
  
  // Stores API (dedicated microservice)
  stores: () => `${getApiUrl('stores')}/api/stores`,
  store: (storeId: string) => `${getApiUrl('stores')}/api/stores/${storeId}`,
  enterpriseStores: (enterpriseId: string) => 
    `${getApiUrl('stores')}/api/stores/enterprise/${enterpriseId}`,
  
  // Dayparts API (dedicated microservice)
  enterpriseDayparts: (enterpriseId: string) =>
    `${getApiUrl('dayparts')}/enterprises/${enterpriseId}/dayparts`,
  daypartTemplates: () => `${getApiUrl('dayparts')}/templates`,
  daypartValidate: () => `${getApiUrl('dayparts')}/validate`,
  
  // Employees API (enhanced HR management)
  employees: (enterpriseId?: string) => {
    const baseUrl = `${getApiUrl('employees')}/api/employees`;
    return enterpriseId ? `${baseUrl}?enterpriseId=${enterpriseId}` : baseUrl;
  },
  employee: (employeeId: string) => 
    `${getApiUrl('employees')}/api/employees/${employeeId}`,
  jobs: (enterpriseId?: string) => {
    const baseUrl = `${getApiUrl('employees')}/api/jobs`;
    return enterpriseId ? `${baseUrl}?enterpriseId=${enterpriseId}` : baseUrl;
  },

  // Labor Rules API (comprehensive US state labor law compliance)
  laborRules: (enterpriseId?: string) => {
    const baseUrl = `${getApiUrl('employees')}/api/labor-rules`;
    return enterpriseId ? `${baseUrl}?enterpriseId=${enterpriseId}` : baseUrl;
  },
  laborRule: (ruleId: string) =>
    `${getApiUrl('employees')}/api/labor-rules/${ruleId}`,
  laborRulesStates: () => `${getApiUrl('employees')}/api/labor-rules/states`,
  laborRulesStateTemplate: (stateCode: string) =>
    `${getApiUrl('employees')}/api/labor-rules/states/${stateCode}`,
  laborRulesTemplates: (ruleType: string) =>
    `${getApiUrl('employees')}/api/labor-rules/templates/${ruleType}`,
  laborRulesApplyStateTemplate: () =>
    `${getApiUrl('employees')}/api/labor-rules/apply-state-template`,
  laborRulesTestOvertime: () =>
    `${getApiUrl('employees')}/api/labor-rules/test-overtime`,
  
  // Menu Ingestion endpoints (dedicated Menu Management microservice)
  menuIngestion: (enterpriseId: string) => 
    `${getApiUrl('menuManagement')}/api/menu-management/ingestion/${enterpriseId}`,
  menuIngestionJob: (jobId: string) => 
    `${getApiUrl('menuManagement')}/api/menu-management/ingestion/${jobId}`,
  menuIngestionStaging: (jobId: string) => 
    `${getApiUrl('menuManagement')}/api/menu-management/staging/${jobId}`,
  menuIngestionApprove: (jobId: string) => 
    `${getApiUrl('menuManagement')}/api/menu-management/staging/${jobId}/approve`,
  
  // Bulk Import endpoints (using ingestion system)
  bulkImportUpload: () => `${getApiUrl('menuManagement')}/api/menu-management/ingestion`,
  bulkImportAnalysis: (analysisId: string) => 
    `${getApiUrl('menuManagement')}/api/menu-management/ingestion/job/${analysisId}`,
  bulkImportProcess: () => `${getApiUrl('menuManagement')}/api/menu-management/ingestion`,
  
  // Note: Organizational Units endpoint doesn't exist in Core Data API
  // The hierarchy is: Enterprise > Concepts > Franchisees > Stores
  // Using franchisees as fallback for organizational units
  organizationalUnits: (enterpriseId: string) => 
    `${getApiUrl('coreData')}/api/enterprises/${enterpriseId}/franchisees`,
  
  // Categories (dedicated microservice)
  categories: (enterpriseId: string) => 
    `${getApiUrl('categories')}/api/enterprises/${enterpriseId}/categories`,
  createCategory: (enterpriseId: string) => 
    `${getApiUrl('categories')}/api/enterprises/${enterpriseId}/categories`,
  updateCategory: (categoryId: string) => 
    `${getApiUrl('categories')}/api/categories/${categoryId}`,
  deleteCategory: (categoryId: string) => 
    `${getApiUrl('categories')}/api/categories/${categoryId}`,
  
  // User Management (uses Core Data API for admin users)
  adminUsers: (scope?: string, scopeId?: string) => {
    let url = `${getApiUrl('coreData')}/api/admin/users`;
    const params: string[] = [];
    if (scope) params.push(`scope=${scope}`);
    if (scopeId) params.push(`scopeId=${scopeId}`);
    return params.length > 0 ? `${url}?${params.join('&')}` : url;
  },
  adminUser: (userId: string) => `${getApiUrl('coreData')}/api/admin/users/${userId}`,
  adminUserRoles: (userId: string) => `${getApiUrl('coreData')}/api/admin/users/${userId}/roles`,
  adminUserRole: (userId: string, roleId: string) => `${getApiUrl('coreData')}/api/admin/users/${userId}/roles/${roleId}`,

  // Support & Documentation endpoints (dedicated Employees microservice)
  support: () => `${getApiUrl('employees')}/api/support`,
  documentation: () => `${getApiUrl('employees')}/api/documentation`,
  
  // Dashboard & Metrics (dedicated Employees microservice) 
  dashboardMetrics: () => `${getApiUrl('employees')}/dashboard/metrics`,
  
  // Inventory endpoints (dedicated Stores microservice)
  inventoryStock: (storeId: string) => 
    `${getApiUrl('stores')}/inventory/stock/${storeId}`,
  
  // Upload endpoints
  uploadImage: () => `${getApiUrl('menuManagement')}/api/upload/image`,
  uploadVideo: () => `${getApiUrl('menuManagement')}/api/upload/video`,
  
  // Social Media Management (dedicated microservice)
  socialMediaAccounts: () => `${getApiUrl('socialMedia')}/api/social-media/accounts`,
  socialMediaAccount: (accountId: string) => `${getApiUrl('socialMedia')}/api/social-media/accounts/${accountId}`,
  socialMediaPosts: () => `${getApiUrl('socialMedia')}/api/social-media/posts`,
  socialMediaPost: (postId: string) => `${getApiUrl('socialMedia')}/api/social-media/posts/${postId}`,
  socialMediaCampaigns: () => `${getApiUrl('socialMedia')}/api/social-media/campaigns`,
  socialMediaCampaign: (campaignId: string) => `${getApiUrl('socialMedia')}/api/social-media/campaigns/${campaignId}`,
  socialMediaAnalytics: () => `${getApiUrl('socialMedia')}/api/social-media/analytics`,
  socialMediaTemplates: () => `${getApiUrl('socialMedia')}/api/social-media/templates`,
  socialMediaGenerateContent: () => `${getApiUrl('socialMedia')}/api/social-media/generate-content`,
  
  // Facebook API endpoints
  facebookPages: () => `${getApiUrl('socialMedia')}/api/facebook/pages`,
  facebookPagePosts: (pageId: string) => `${getApiUrl('socialMedia')}/api/facebook/pages/${pageId}/posts`,
  facebookSyncPosts: (pageId: string) => `${getApiUrl('socialMedia')}/api/facebook/pages/${pageId}/sync-posts`,
  facebookPageInsights: (pageId: string) => `${getApiUrl('socialMedia')}/api/facebook/pages/${pageId}/insights`,
  facebookOAuthExchangeToken: () => `${getApiUrl('socialMedia')}/api/facebook/oauth/exchange-token`,
  facebookOAuthExchangeCode: () => `${getApiUrl('socialMedia')}/api/facebook/oauth/exchange-code`,
  
  // LinkedIn API endpoints
  linkedinAuth: () => `${getApiUrl('socialMedia')}/api/social-media/linkedin/auth`,
  linkedinStatus: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/status?storeId=${storeId}`,
  linkedinDisconnect: () => `${getApiUrl('socialMedia')}/api/social-media/linkedin/disconnect`,
  linkedinPost: () => `${getApiUrl('socialMedia')}/api/social-media/linkedin/post`,
  linkedinPosts: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/posts?storeId=${storeId}`,
  linkedinAnalytics: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/analytics?storeId=${storeId}`,
  linkedinDemographics: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/demographics?storeId=${storeId}`,
  linkedinCampaigns: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/campaigns?storeId=${storeId}`,
  linkedinCampaign: (campaignId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/campaigns/${campaignId}`,
  linkedinCampaignMetrics: (campaignId: string, storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/campaigns/${campaignId}/metrics?storeId=${storeId}`,
  linkedinLeadForms: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/lead-forms?storeId=${storeId}`,
  linkedinLeads: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/leads?storeId=${storeId}`,
  linkedinLeadScore: (leadId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/leads/${leadId}/score`,
  linkedinLeadsExport: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/leads/export?storeId=${storeId}`,
  linkedinAudiences: (storeId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/audiences?storeId=${storeId}`,
  linkedinAudience: (audienceId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/audiences/${audienceId}`,
  linkedinAudienceSync: (audienceId: string) => `${getApiUrl('socialMedia')}/api/social-media/linkedin/audiences/${audienceId}/sync`,

  // ABM (Account-Based Marketing) endpoints
  abmCompanyLists: () => `${getApiUrl('socialMedia')}/api/admin/abm/company-lists`,
  abmCompanyList: (listId: string) => `${getApiUrl('socialMedia')}/api/admin/abm/company-lists/${listId}`,
  abmCompanyListImport: (listId: string) => `${getApiUrl('socialMedia')}/api/admin/abm/company-lists/${listId}/import`,
  abmCompanyListSync: (listId: string) => `${getApiUrl('socialMedia')}/api/admin/abm/company-lists/${listId}/sync`,
  abmCampaigns: () => `${getApiUrl('socialMedia')}/api/admin/abm/campaigns`,
  abmCampaign: (campaignId: string) => `${getApiUrl('socialMedia')}/api/admin/abm/campaigns/${campaignId}`,
  abmCampaignPublish: (campaignId: string) => `${getApiUrl('socialMedia')}/api/admin/abm/campaigns/${campaignId}/publish`,
  abmCampaignMetrics: (campaignId: string) => `${getApiUrl('socialMedia')}/api/admin/abm/campaigns/${campaignId}/metrics`,
  abmCampaignAccounts: (campaignId: string) => `${getApiUrl('socialMedia')}/api/admin/abm/campaigns/${campaignId}/accounts`,
  abmGenerateSequence: () => `${getApiUrl('socialMedia')}/api/admin/abm/campaigns/generate-sequence`,

  // Discount Management (dedicated microservice with DynamoDB)
  enterpriseDiscounts: (enterpriseId: string) => `${getApiUrl('discounts')}/api/enterprises/${enterpriseId}/discounts`,
  createDiscount: (enterpriseId: string) => `${getApiUrl('discounts')}/api/enterprises/${enterpriseId}/discounts`,
  updateDiscount: (discountId: string) => `${getApiUrl('discounts')}/api/discounts/${discountId}`,
  deleteDiscount: (discountId: string) => `${getApiUrl('discounts')}/api/discounts/${discountId}`,
  
  // Discount item/category restrictions
  discountMenuItems: (enterpriseId: string) => `${getApiUrl('discounts')}/api/enterprises/${enterpriseId}/menu-items`,
  discountCategories: (enterpriseId: string) => `${getApiUrl('discounts')}/api/enterprises/${enterpriseId}/categories`,
  
  // Discount Code Management (handled by social media microservice)
  discountCodes: () => `${getApiUrl('socialMedia')}/api/social-media/discount-codes`,
  discountCode: (codeId: string) => `${getApiUrl('socialMedia')}/api/social-media/discount-codes/${codeId}`,
  generateDiscountCodes: () => `${getApiUrl('socialMedia')}/api/social-media/discount-codes/generate`,
  validateDiscountCode: (code: string) => `${getApiUrl('socialMedia')}/api/social-media/discount-codes/validate/${code}`,
  redeemDiscountCode: () => `${getApiUrl('socialMedia')}/api/social-media/discount-codes/redeem`,

  // Discount Assignments API (hierarchy-based assignments with effective dates)
  discountAssignments: (enterpriseId: string) => `${getApiUrl('discounts')}/assignments/${enterpriseId}`,
  discountAssignment: (assignmentId: string) => `${getApiUrl('discounts')}/assignments/${assignmentId}`,
  createDiscountAssignment: () => `${getApiUrl('discounts')}/assignments`,
  activeDiscountAssignments: (enterpriseId: string) => `${getApiUrl('discounts')}/assignments/${enterpriseId}/active`,
  hierarchyDiscountAssignments: (enterpriseId: string, level: string, targetId: string) => 
    `${getApiUrl('discounts')}/assignments/${enterpriseId}/hierarchy/${level}/${targetId}`,
  recordDiscountUsage: (assignmentId: string) => `${getApiUrl('discounts')}/assignments/${assignmentId}/usage`,

  // Security Audit & Compliance Management
  securityAudits: (storeId: string) => `${getApiUrl('securityAudit')}/api/security-audit/stores/${storeId}/audits`,
  securityAudit: (auditId: string) => `${getApiUrl('securityAudit')}/api/security-audit/audits/${auditId}`,
  securityAutomatedChecks: (storeId: string) => `${getApiUrl('securityAudit')}/api/security-audit/automated-checks/${storeId}`,
  securityComplianceReport: (auditId: string) => `${getApiUrl('securityAudit')}/api/security-audit/compliance-report/${auditId}`,
  securityDocumentUpload: () => `${getApiUrl('securityAudit')}/api/security-audit/documents/upload`,
  securityDocument: (documentId: string) => `${getApiUrl('securityAudit')}/api/security-audit/documents/${documentId}`,

  // Contact Form Submission
  contactForm: () => `${getApiUrl('contactForm')}/api/contact`,

  // Ubiquiti Network Management
  networkSites: () => `${getApiUrl('ubiquitiNetwork')}/api/network/sites`,
  networkSite: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/sites/${restaurantId}`,
  networkStats: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/stats/${restaurantId}`,
  networkClients: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/clients/${restaurantId}`,
  networkDevices: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/devices/${restaurantId}`,
  networkHealth: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/health/${restaurantId}`,
  networkFirewall: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/firewall/${restaurantId}`,
  networkAuthorize: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/authorize/${restaurantId}`,
  networkBlock: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/block/${restaurantId}`,

  // UniFi Video System
  videoCameras: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/cameras/${restaurantId}`,
  videoCamera: (restaurantId: string, cameraId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/cameras/${restaurantId}/${cameraId}`,
  videoSnapshot: (restaurantId: string, cameraId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/snapshot/${restaurantId}/${cameraId}`,
  videoRecordings: (restaurantId: string, cameraId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/recordings/${restaurantId}/${cameraId}`,
  videoRecordingStart: (restaurantId: string, cameraId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/recording/start/${restaurantId}/${cameraId}`,
  videoRecordingStop: (restaurantId: string, cameraId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/recording/stop/${restaurantId}/${cameraId}`,
  videoEvents: (restaurantId: string, cameraId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/events/${restaurantId}/${cameraId}`,
  videoSystemInfo: (restaurantId: string) => `${getApiUrl('ubiquitiNetwork')}/api/network/video/info/${restaurantId}`,

  // Restaurant Messaging & Alert System
  messaging: {
    url: `${API_CONFIG.microservices.messaging.url}`,
    websocketUrl: process.env.REACT_APP_MESSAGING_WEBSOCKET_URL || '', // Disabled until WebSocket API Gateway is properly configured
    sendAlert: () => `${API_CONFIG.microservices.messaging.url}/api/alerts/send`,
    messageHistory: (storeId: string) => `${API_CONFIG.microservices.messaging.url}/api/messages/history/${storeId}`,
    markAsRead: (messageId: string) => `${API_CONFIG.microservices.messaging.url}/api/messages/${messageId}/read`,
    alertTypes: () => `${API_CONFIG.microservices.messaging.url}/api/alerts/types`,
    health: () => `${API_CONFIG.microservices.messaging.url}/health`,
    // Database sync endpoints for POS devices
    syncMessages: (storeId: string, lastSync?: string) => {
      const baseUrl = `${API_CONFIG.microservices.messaging.url}/api/messages/sync/${storeId}`;
      return lastSync ? `${baseUrl}?lastSync=${lastSync}` : baseUrl;
    },
    acknowledgeMessage: (messageId: string) => `${API_CONFIG.microservices.messaging.url}/api/messages/${messageId}/acknowledge`,
    posMessages: (storeId: string) => `${API_CONFIG.microservices.messaging.url}/api/pos/messages/${storeId}`,
    clearStoreMessages: (storeId: string) => `${API_CONFIG.microservices.messaging.url}/api/messages/store/${storeId}`
  },

  // Digital Menu Boards Management
  menuBoards: () => `${getApiUrl('menuBoards')}/api/menu-boards`,
  menuBoard: (menuBoardId: string) => `${getApiUrl('menuBoards')}/api/menu-boards/${menuBoardId}`,
  menuBoardsByEnterprise: (enterpriseId: string) => `${getApiUrl('menuBoards')}/api/menu-boards/enterprise/${enterpriseId}`,
  menuBoardsByLocation: (locationId: string) => `${getApiUrl('menuBoards')}/api/menu-boards/location/${locationId}`,
  menuBoardItems: (menuBoardId: string) => `${getApiUrl('menuBoards')}/api/menu-boards/${menuBoardId}/items`,
  menuBoardItem: (itemId: string) => `${getApiUrl('menuBoards')}/api/menu-board-items/${itemId}`,
  menuBoardMedia: () => `${getApiUrl('menuBoards')}/api/menu-boards/media`,
  menuBoardTemplates: () => `${getApiUrl('menuBoards')}/api/menu-boards/templates`,
  
  // Employee Scheduling & Time Tracking
  schedules: (params?: { employeeId?: string; storeId?: string }) => {
    const baseUrl = `${getApiUrl('scheduling')}/api/schedules`;
    if (!params) return baseUrl;
    const queryParams = new URLSearchParams();
    if (params.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params.storeId) queryParams.append('storeId', params.storeId);
    const queryString = queryParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  schedule: (scheduleId: string) => `${getApiUrl('scheduling')}/api/schedules/${scheduleId}`,
  
  // Schedule Templates & AI Optimization  
  scheduleTemplates: (storeId?: string) => {
    const baseUrl = `${getApiUrl('scheduling')}/api/schedules/templates`;
    return storeId ? `${baseUrl}?storeId=${storeId}` : baseUrl;
  },
  scheduleTemplate: (templateId: string) => `${getApiUrl('scheduling')}/api/schedules/templates/${templateId}`,
  scheduleTemplateAiOptimize: () => `${getApiUrl('scheduling')}/api/schedules/ai-optimize`,
  scheduleRequests: (employeeId?: string) => {
    const baseUrl = `${getApiUrl('scheduling')}/api/schedule-requests`;
    return employeeId ? `${baseUrl}?employeeId=${employeeId}` : baseUrl;
  },
  scheduleRequest: (requestId: string) => `${getApiUrl('scheduling')}/api/schedule-requests/${requestId}`,
  timeEntries: (employeeId?: string) => {
    const baseUrl = `${getApiUrl('scheduling')}/api/time-entries`;
    return employeeId ? `${baseUrl}?employeeId=${employeeId}` : baseUrl;
  },
  timeEntry: (timeEntryId: string) => `${getApiUrl('scheduling')}/api/time-entries/${timeEntryId}`,
  performanceMetrics: (employeeId: string) => `${getApiUrl('scheduling')}/api/performance-metrics/${employeeId}`,
  clockIn: () => `${getApiUrl('scheduling')}/api/time-entries`,
  clockOut: (timeEntryId: string) => `${getApiUrl('scheduling')}/api/time-entries/${timeEntryId}`,

  // POS Synchronization
  posHealthCheck: () => `${getApiUrl('posSync')}/health`,
  posStoreDiscounts: (storeId: string) => `${getApiUrl('posSync')}/api/pos/sync/discounts/${storeId}`,
  posSyncOrders: () => `${getApiUrl('posSync')}/api/pos/sync/orders`,
  posStoreDevices: (storeId: string) => `${getApiUrl('posSync')}/api/pos/devices/${storeId}`,
  posRegisterDevice: () => `${getApiUrl('posSync')}/api/pos/devices/register`,

  // Table Chart Management
  tableLibrary: () => `${getApiUrl('tableCharts')}/api/table-library`,
  restaurantAreas: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/areas`,
  restaurantArea: (restaurantId: string, areaId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/areas/${areaId}`,
  restaurantTables: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/tables`,
  restaurantTable: (restaurantId: string, tableId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/tables/${tableId}`,
  restaurantLayouts: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/layouts`,
  restaurantLayout: (restaurantId: string, layoutId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/layouts/${layoutId}`,
  joinTables: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/tables/join`,
  splitTables: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/tables/split`,
  
  // Table Status for POS Integration
  tableStatuses: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/tables/status`,
  updateTableStatus: (restaurantId: string, tableId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/tables/${tableId}/status`,

  // Walls
  restaurantWalls: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/walls`,
  restaurantWall: (restaurantId: string, wallId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/walls/${wallId}`,

  // Fixtures
  restaurantFixtures: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/fixtures`,
  restaurantFixture: (restaurantId: string, fixtureId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/fixtures/${fixtureId}`,

  // Section Templates
  sectionTemplates: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/section-templates`,
  sectionTemplate: (restaurantId: string, templateId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/section-templates/${templateId}`,
  applySectionTemplate: (restaurantId: string, templateId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/section-templates/${templateId}/apply`,

  // Floor Config
  floorConfig: (restaurantId: string) => `${getApiUrl('tableCharts')}/restaurants/${restaurantId}/floor-config`,
  
  // Weather Analytics & Data Collection
  weatherCollect: () => `${getApiUrl('weather')}/collect`,
  weatherCollectForecast: () => `${getApiUrl('weather')}/collect-forecast`,
  weatherCollectHourly: () => `${getApiUrl('weather')}/collect-hourly`,
  weatherStore: () => `${getApiUrl('weather')}/store`,
  weatherByStore: (storeId: string) => `${getApiUrl('weather')}/store/${storeId}`,
  weatherByDate: (date: string) => `${getApiUrl('weather')}/date/${date}`,
  weatherTrends: () => `${getApiUrl('weather')}/trends`,
  weatherForecast: (storeId: string) => `${getApiUrl('weather')}/forecast/${storeId}`,
  weatherGeocode: () => `${getApiUrl('weather')}/geocode`,
  weatherManualRecord: () => `${getApiUrl('weather')}/manual-record`,
  
  // Waitlist Management API
  waitlistEntries: (storeId: string) => `${getApiUrl('waitlist')}/waitlist/${storeId}`,
  waitlistAdd: (storeId: string) => `${getApiUrl('waitlist')}/waitlist/${storeId}/add`,
  waitlistEntry: (storeId: string, waitlistId: string) => 
    `${getApiUrl('waitlist')}/waitlist/${storeId}/update/${waitlistId}`,
  waitlistNotify: (storeId: string, waitlistId: string) => 
    `${getApiUrl('waitlist')}/waitlist/${storeId}/notify/${waitlistId}`,
  waitlistTables: (storeId: string) => `${getApiUrl('waitlist')}/tables/${storeId}`,
  waitlistTableStatus: (storeId: string, tableId: string) => 
    `${getApiUrl('waitlist')}/tables/${storeId}/update/${tableId}`,
  
  // Payment Processing API
  paymentHealth: () => `${getApiUrl('payment')}/health`,
  paymentLinks: () => `${getApiUrl('payment')}/api/payment-links`,
  paymentLink: (paymentId: string) => `${getApiUrl('payment')}/api/payment-links/${paymentId}`,
  paymentProcess: () => `${getApiUrl('payment')}/api/payments/process`,
  paymentLinkValidate: (paymentId: string, token: string) =>
    `${getApiUrl('payment')}/api/payment-links/${paymentId}/validate?token=${encodeURIComponent(token)}`,
  validatePayment: () => `${getApiUrl('payment')}/api/payment-links/validate`,
  
  // AI Chat Assistant API
  aiChatSend: () => `${getApiUrl('aiChat')}/`,
  aiChatHealth: () => `${getApiUrl('aiChat')}/health`,

  // Support Chat API (conversation-based with persistence and escalation)
  supportConversations: () => `${getApiUrl('supportChat')}/api/support/conversations`,
  supportConversation: (conversationId: string) => `${getApiUrl('supportChat')}/api/support/conversations/${conversationId}`,
  supportMessage: (conversationId: string) => `${getApiUrl('supportChat')}/api/support/conversations/${conversationId}/messages`,
  supportEscalate: (conversationId: string) => `${getApiUrl('supportChat')}/api/support/conversations/${conversationId}/escalate`,
  supportResolve: (conversationId: string) => `${getApiUrl('supportChat')}/api/support/conversations/${conversationId}/resolve`,

  // Voice Ordering Session Management API
  voiceSessions: () => `${getApiUrl('voiceOrdering')}/sessions`,
  voiceSession: (sessionId: string) => `${getApiUrl('voiceOrdering')}/sessions/${sessionId}`,
  voiceSessionUtterance: (sessionId: string) => `${getApiUrl('voiceOrdering')}/sessions/${sessionId}/utterance`,
  voiceSessionComplete: (sessionId: string) => `${getApiUrl('voiceOrdering')}/sessions/${sessionId}/complete`,
  voiceSimulate: () => `${getApiUrl('voiceOrdering')}/simulate`,

  // Voice Configuration API
  voiceConfig: (storeId: string) => `${getApiUrl('voiceConfig')}/config/${storeId}`,
  voiceDevices: (storeId: string) => `${getApiUrl('voiceConfig')}/devices/${storeId}`,
  voiceDevice: (storeId: string, deviceId: string) => `${getApiUrl('voiceConfig')}/devices/${storeId}/${deviceId}`,
  voiceDeviceSync: (storeId: string, deviceId: string) => `${getApiUrl('voiceConfig')}/devices/${storeId}/${deviceId}/sync`,
  voicePhrases: (storeId: string) => `${getApiUrl('voiceConfig')}/phrases/${storeId}`,
  voicePhrase: (storeId: string, phraseKey: string) => `${getApiUrl('voiceConfig')}/phrases/${storeId}/${phraseKey}`,
  voiceAnalytics: (storeId: string) => `${getApiUrl('voiceConfig')}/analytics/${storeId}`,

  // Phone AI Assistant API (AWS Connect/Lex integration)
  phoneConfig: (storeId: string) => `${getApiUrl('phoneAssistant')}/config/${storeId}`,
  phoneProvision: (storeId: string) => `${getApiUrl('phoneAssistant')}/provision/${storeId}`,
  phoneCalls: (storeId: string) => `${getApiUrl('phoneAssistant')}/calls/${storeId}`,
  phoneCall: (callId: string) => `${getApiUrl('phoneAssistant')}/calls/${callId}`,
  phoneAnalytics: (storeId: string) => `${getApiUrl('phoneAssistant')}/analytics/${storeId}`,

  // Equipment Monitoring API (Restaurant equipment monitoring and reporting)
  equipmentDevices: (params?: { storeId?: string; enterpriseId?: string; manufacturer?: string; status?: string }) => {
    const baseUrl = `${getApiUrl('equipmentMonitoring')}/devices`;
    if (!params) return baseUrl;
    const queryParams = new URLSearchParams();
    if (params.storeId) queryParams.append('storeId', params.storeId);
    if (params.enterpriseId) queryParams.append('enterpriseId', params.enterpriseId);
    if (params.manufacturer) queryParams.append('manufacturer', params.manufacturer);
    if (params.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  equipmentDevice: (deviceId: string, enterpriseId?: string) => {
    const baseUrl = `${getApiUrl('equipmentMonitoring')}/devices/${deviceId}`;
    return enterpriseId ? `${baseUrl}?enterpriseId=${enterpriseId}` : baseUrl;
  },
  equipmentMetrics: (deviceId: string) => `${getApiUrl('equipmentMonitoring')}/metrics/${deviceId}`,
  equipmentAlerts: (params?: { storeId?: string; deviceId?: string; severity?: string }) => {
    const baseUrl = `${getApiUrl('equipmentMonitoring')}/alerts`;
    if (!params) return baseUrl;
    const queryParams = new URLSearchParams();
    if (params.storeId) queryParams.append('storeId', params.storeId);
    if (params.deviceId) queryParams.append('deviceId', params.deviceId);
    if (params.severity) queryParams.append('severity', params.severity);
    const queryString = queryParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  equipmentDashboard: (storeId: string) => `${getApiUrl('equipmentMonitoring')}/dashboard/${storeId}`,
  equipmentCredentials: () => `${getApiUrl('equipmentMonitoring')}/credentials`,
  equipmentWebhook: (manufacturer: string) => `${getApiUrl('equipmentMonitoring')}/webhook/${manufacturer}`,
  equipmentSensusairConnect: () => `${getApiUrl('equipmentMonitoring')}/sensusair/connect`,
  equipmentSensusairSpaces: (enterpriseId: string) => `${getApiUrl('equipmentMonitoring')}/sensusair/spaces?enterpriseId=${enterpriseId}`,
  equipmentSensusairSync: (enterpriseId: string) => `${getApiUrl('equipmentMonitoring')}/sensusair/sync?enterpriseId=${enterpriseId}`,
  equipmentSensusairReadings: (sensorId: string, enterpriseId: string) => `${getApiUrl('equipmentMonitoring')}/sensusair/readings/${sensorId}?enterpriseId=${enterpriseId}`,

  // POS Integration API (Toast adapter, order polling, connection management)
  posConnections: (storeId?: string) =>
    storeId
      ? `${getApiUrl('posIntegration')}/api/pos/connections/${storeId}`
      : `${getApiUrl('posIntegration')}/api/pos/connections`,
  posConnectionUpdate: (storeId: string, posType: string) =>
    `${getApiUrl('posIntegration')}/api/pos/connections/${storeId}/${posType}`,
  posConnectionTest: (storeId: string) =>
    `${getApiUrl('posIntegration')}/api/pos/connections/${storeId}/test`,
  posOrders: (storeId: string) =>
    `${getApiUrl('posIntegration')}/api/pos/orders/${storeId}`,
  posPollToast: () =>
    `${getApiUrl('posIntegration')}/api/pos/poll/toast`,

  // Review Management API (Google Reviews, AI response generation)
  reviews: (storeId: string) =>
    `${getApiUrl('reviewManagement')}/api/reviews/${storeId}`,
  reviewStats: (storeId: string) =>
    `${getApiUrl('reviewManagement')}/api/reviews/${storeId}/stats`,
  reviewGenerateReply: (reviewId: string) =>
    `${getApiUrl('reviewManagement')}/api/reviews/${reviewId}/generate-reply`,
  reviewApprove: (reviewId: string) =>
    `${getApiUrl('reviewManagement')}/api/reviews/${reviewId}/approve`,
  reviewEditReply: (reviewId: string) =>
    `${getApiUrl('reviewManagement')}/api/reviews/${reviewId}/edit-reply`,
  reviewPostReply: (reviewId: string) =>
    `${getApiUrl('reviewManagement')}/api/reviews/${reviewId}/post-reply`,
  reviewConfig: (storeId: string) =>
    `${getApiUrl('reviewManagement')}/api/reviews/config/${storeId}`,
  reviewPollGoogle: () =>
    `${getApiUrl('reviewManagement')}/api/reviews/poll/google`,

  // Review Trigger API (review request scheduling, micro-survey)
  reviewRequests: (storeId: string) =>
    `${getApiUrl('reviewTrigger')}/api/review-requests/${storeId}`,
  reviewRequestStats: (storeId: string) =>
    `${getApiUrl('reviewTrigger')}/api/review-requests/${storeId}/stats`,
  reviewRequestOptOut: (contact: string) =>
    `${getApiUrl('reviewTrigger')}/api/review-requests/opt-out/${encodeURIComponent(contact)}`,
  surveyContext: (requestId: string) =>
    `${getApiUrl('reviewTrigger')}/api/survey/${requestId}`,
  surveyWalkIn: (storeId: string) =>
    `${getApiUrl('reviewTrigger')}/api/survey/store/${storeId}`,
  surveyRespond: () =>
    `${getApiUrl('reviewTrigger')}/api/survey/respond`,
  reviewTriggerProcess: () =>
    `${getApiUrl('reviewTrigger')}/api/review-trigger/process`,
  reviewTriggerSend: () =>
    `${getApiUrl('reviewTrigger')}/api/review-trigger/send`,

  // Guest Recovery System
  recoveryMyStores: () =>
    `${getApiUrl('reviewManagement')}/api/recovery/my-stores`,
  recoveryConfig: (storeId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/config/${storeId}`,
  recoveryMenuPool: (storeId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/config/${storeId}/menu-pool`,
  recoveryWorkflows: (storeId: string, status?: string) => {
    const base = `${getApiUrl('reviewManagement')}/api/recovery/workflows?storeId=${storeId}`;
    return status ? `${base}&status=${status}` : base;
  },
  recoveryWorkflow: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}`,
  recoveryApprove: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/approve`,
  recoveryEdit: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/edit`,
  recoveryChangeTier: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/change-tier`,
  recoveryReplyOnly: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/reply-only`,
  recoveryFollowUp: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/follow-up`,
  recoverySnooze: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/snooze`,
  recoveryEscalate: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/escalate`,
  recoveryClose: (workflowId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/workflows/${workflowId}/close`,
  recoveryAnalytics: (storeId: string, period?: string) => {
    const base = `${getApiUrl('reviewManagement')}/api/recovery/analytics/${storeId}`;
    return period ? `${base}?period=${period}` : base;
  },
  recoveryScorecard: (storeId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/scorecard/${storeId}`,
  recoveryDigest: (storeId: string) =>
    `${getApiUrl('reviewManagement')}/api/recovery/digest/${storeId}`,

  // Billing & Subscription Management
  billingCheckout: () => `${getApiUrl('billing')}/api/billing/checkout-session`,
  billingPortal: () => `${getApiUrl('billing')}/api/billing/portal-session`,
  billingHealth: () => `${getApiUrl('billing')}/api/billing/health`,

  // Promo Code Management (Admin)
  promoCodeGenerate: () => `${getApiUrl('billing')}/api/admin/promo-codes/generate`,
  promoCodeGenerateBulk: () => `${getApiUrl('billing')}/api/admin/promo-codes/generate-bulk`,
  promoCodes: () => `${getApiUrl('billing')}/api/admin/promo-codes`,
  promoCode: (promoCodeId: string) => `${getApiUrl('billing')}/api/admin/promo-codes/${promoCodeId}`,
  promoCodeDeactivate: (promoCodeId: string) => `${getApiUrl('billing')}/api/admin/promo-codes/${promoCodeId}/deactivate`,
  promoCampaigns: () => `${getApiUrl('billing')}/api/admin/promo-codes/campaigns`,
  promoCampaign: (campaignId: string) => `${getApiUrl('billing')}/api/admin/promo-codes/campaigns/${encodeURIComponent(campaignId)}`,
  promoAnalytics: () => `${getApiUrl('billing')}/api/admin/promo-codes/analytics`,
  promoExport: () => `${getApiUrl('billing')}/api/admin/promo-codes/export`,

  // Command Center Analytics API
  // Note: getApiUrl('commandCenter') already includes /api/command-center
  commandCenterOverview: (storeId: string) =>
    `${getApiUrl('commandCenter')}/${storeId}/overview`,
  commandCenterRevenue: (storeId: string, timeframe?: string) => {
    const base = `${getApiUrl('commandCenter')}/${storeId}/revenue`;
    return timeframe ? `${base}?timeframe=${timeframe}` : base;
  },
  commandCenterReputation: (storeId: string) =>
    `${getApiUrl('commandCenter')}/${storeId}/reputation`,
  commandCenterSocial: (storeId: string) =>
    `${getApiUrl('commandCenter')}/${storeId}/social`,
  commandCenterTeam: (storeId: string) =>
    `${getApiUrl('commandCenter')}/${storeId}/team`,
  commandCenterMenuPerformance: (storeId: string) =>
    `${getApiUrl('commandCenter')}/${storeId}/menu-performance`,
  commandCenterEnterpriseOverview: (enterpriseId: string) =>
    `${getApiUrl('commandCenter')}/enterprise/${enterpriseId}/overview`,

  // AI Suggestion Engine API
  // Note: getApiUrl('suggestionEngine') already includes /api/suggestions
  suggestions: (storeId: string) =>
    `${getApiUrl('suggestionEngine')}/${storeId}`,
  suggestionFeedback: (storeId: string, suggestionId: string) =>
    `${getApiUrl('suggestionEngine')}/${storeId}/${suggestionId}`,

  // Ask BSO Conversational Analytics API
  // Note: getApiUrl('askBso') already includes /api/ask-bso
  askBsoQuery: () =>
    `${getApiUrl('askBso')}/query`,
  askBsoHealth: () =>
    `${getApiUrl('askBso')}/health`,

  // CRM Management
  crmContacts: (status?: string) => {
    const baseUrl = `${getApiUrl('socialMedia')}/api/crm/contacts`;
    return status ? `${baseUrl}?status=${encodeURIComponent(status)}` : baseUrl;
  },
  crmContact: (contactId: string) =>
    `${getApiUrl('socialMedia')}/api/crm/contacts/${contactId}`,
  crmContactActivity: (contactId: string) =>
    `${getApiUrl('socialMedia')}/api/crm/contacts/${contactId}/activity`,
  crmStats: () =>
    `${getApiUrl('socialMedia')}/api/crm/stats`,

  // ---------- Intelligent Inventory endpoints ----------
  // Core (stock, counts, snapshots, waste, ingredients)
  inventoryStockLevels: (storeId: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${storeId}/stock`,
  inventoryCount: (storeId: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${storeId}/count`,
  inventorySnapshots: (storeId: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${storeId}/snapshots`,
  inventoryConfigUpdate: (storeId: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${storeId}/config`,
  inventoryWaste: (storeId: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${storeId}/waste`,
  inventoryWasteReport: (storeId: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${storeId}/waste/report`,
  ingredients: (enterpriseId: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${enterpriseId}/ingredients`,
  ingredient: (enterpriseId: string, id: string) =>
    `${getApiUrl('inventoryCore')}/api/inventory/${enterpriseId}/ingredients/${id}`,

  // Recipes
  recipes: (enterpriseId: string) =>
    `${getApiUrl('recipes')}/api/recipes/${enterpriseId}`,
  recipe: (enterpriseId: string, recipeId: string) =>
    `${getApiUrl('recipes')}/api/recipes/${enterpriseId}/${recipeId}`,
  recipeExplode: (enterpriseId: string, recipeId: string) =>
    `${getApiUrl('recipes')}/api/recipes/${enterpriseId}/${recipeId}/explode`,

  // Forecast
  inventoryForecast: (storeId: string) =>
    `${getApiUrl('inventoryForecast')}/api/inventory-forecast/${storeId}`,
  inventoryForecastRefresh: (storeId: string) =>
    `${getApiUrl('inventoryForecast')}/api/inventory-forecast/${storeId}/refresh`,

  // Purchase Orders
  purchaseOrders: (storeId: string) =>
    `${getApiUrl('inventoryOrders')}/api/inventory-orders/${storeId}`,
  generatePurchaseOrder: (storeId: string) =>
    `${getApiUrl('inventoryOrders')}/api/inventory-orders/${storeId}/generate`,
  approvePurchaseOrder: (storeId: string, poId: string) =>
    `${getApiUrl('inventoryOrders')}/api/inventory-orders/${storeId}/${poId}/approve`,
  submitPurchaseOrder: (storeId: string, poId: string) =>
    `${getApiUrl('inventoryOrders')}/api/inventory-orders/${storeId}/${poId}/submit`,

  // Receiving
  inventoryReceiving: (storeId: string) =>
    `${getApiUrl('inventoryReceiving')}/api/inventory-receiving/${storeId}`,
  inventoryReceivingDelivery: (storeId: string, deliveryId: string) =>
    `${getApiUrl('inventoryReceiving')}/api/inventory-receiving/${storeId}/${deliveryId}`,

  // Invoices
  invoiceScan: (storeId: string) =>
    `${getApiUrl('inventoryInvoices')}/api/inventory-invoices/${storeId}/scan`,
  invoiceMatch: (storeId: string, invoiceId: string) =>
    `${getApiUrl('inventoryInvoices')}/api/inventory-invoices/${storeId}/${invoiceId}/match`,

  // Vision
  visionCount: () =>
    `${getApiUrl('inventoryVision')}/api/inventory-vision/count`,
  visionReceiving: () =>
    `${getApiUrl('inventoryVision')}/api/inventory-vision/receiving`,
  visionFeedback: () =>
    `${getApiUrl('inventoryVision')}/api/inventory-vision/feedback`,
  visionZones: (storeId: string) =>
    `${getApiUrl('inventoryVision')}/api/inventory-vision/zones/${storeId}`,

  // Suppliers (new dedicated microservice)
  supplier: (enterpriseId: string, supplierId: string) =>
    `${getApiUrl('suppliers')}/api/suppliers/${enterpriseId}/${supplierId}`,
  suppliers: (enterpriseId: string) =>
    `${getApiUrl('suppliers')}/api/suppliers/${enterpriseId}`,
  supplierPerformance: (supplierId: string) =>
    `${getApiUrl('suppliers')}/api/suppliers/${supplierId}/performance`,

  // Network-wide inventory alerts
  networkAlerts: () =>
    `${getApiUrl('inventoryNetwork')}/api/inventory-network/alerts`,

  // Waste analytics
  wasteAnalysis: (storeId: string) =>
    `${getApiUrl('inventoryWaste')}/api/inventory-waste/${storeId}`,

  // Hierarchical pricing
  pricingResolve: (menuItemId: string, storeId: string, enterpriseId: string) =>
    `${getApiUrl('pricing')}/api/pricing/resolve/${menuItemId}?storeId=${encodeURIComponent(storeId)}&enterpriseId=${encodeURIComponent(enterpriseId)}`,
  pricingOverrides: (menuItemId: string) =>
    `${getApiUrl('pricing')}/api/pricing/overrides/${menuItemId}`,
  pricingSet: () =>
    `${getApiUrl('pricing')}/api/pricing/set`,
  pricingRemove: () =>
    `${getApiUrl('pricing')}/api/pricing/remove`,

  // Universal modifiers
  modifierGroups: (enterpriseId: string, includeInactive = false) =>
    `${getApiUrl('modifiers')}/api/modifiers/groups?enterpriseId=${encodeURIComponent(enterpriseId)}${includeInactive ? '&includeInactive=true' : ''}`,
  modifierGroupsCreate: () =>
    `${getApiUrl('modifiers')}/api/modifiers/groups`,
  modifierGroup: (groupId: string) =>
    `${getApiUrl('modifiers')}/api/modifiers/groups/${encodeURIComponent(groupId)}`,
  modifierGroupOptions: (groupId: string, includeInactive = false) =>
    `${getApiUrl('modifiers')}/api/modifiers/groups/${encodeURIComponent(groupId)}/options${includeInactive ? '?includeInactive=true' : ''}`,
  modifierGroupOptionsReorder: (groupId: string) =>
    `${getApiUrl('modifiers')}/api/modifiers/groups/${encodeURIComponent(groupId)}/options/reorder`,
  modifierOption: (optionId: string, groupId?: string) =>
    `${getApiUrl('modifiers')}/api/modifiers/options/${encodeURIComponent(optionId)}${groupId ? `?groupId=${encodeURIComponent(groupId)}` : ''}`,
  modifierResolveForMenuItem: (menuItemId: string) =>
    `${getApiUrl('modifiers')}/api/modifiers/menu-item/${encodeURIComponent(menuItemId)}/resolved`,
  modifierValidate: () =>
    `${getApiUrl('modifiers')}/api/modifiers/validate`,
  modifierTemplates: () =>
    `${getApiUrl('modifiers')}/api/modifiers/templates`,
  modifierTemplateImport: (templateId: string) =>
    `${getApiUrl('modifiers')}/api/modifiers/templates/${encodeURIComponent(templateId)}/import`,
  modifierGroupStoreOverride: (groupId: string, storeId: string) =>
    `${getApiUrl('modifiers')}/api/modifiers/groups/${encodeURIComponent(groupId)}/store-override/${encodeURIComponent(storeId)}`,
  modifierOptionStoreOverride: (optionId: string, storeId: string) =>
    `${getApiUrl('modifiers')}/api/modifiers/options/${encodeURIComponent(optionId)}/store-override/${encodeURIComponent(storeId)}`,

  // Approvals (voids/discounts/refunds/comps/price overrides)
  approvalsCreate: () =>
    `${getApiUrl('approvals')}/api/approvals`,
  approvalsForStore: (storeId: string, status?: string, limit = 100) => {
    const params = new URLSearchParams({ storeId, limit: String(limit) });
    if (status) params.set('status', status);
    return `${getApiUrl('approvals')}/api/approvals?${params.toString()}`;
  },
  approvalsForEnterprise: (enterpriseId: string, status?: string) =>
    `${getApiUrl('approvals')}/api/approvals/enterprise/${encodeURIComponent(enterpriseId)}${status ? `?status=${encodeURIComponent(status)}` : ''}`,
  approvalsForEmployee: (employeeId: string, status?: string) =>
    `${getApiUrl('approvals')}/api/approvals/employee/${encodeURIComponent(employeeId)}${status ? `?status=${encodeURIComponent(status)}` : ''}`,
  approval: (approvalId: string) =>
    `${getApiUrl('approvals')}/api/approvals/${encodeURIComponent(approvalId)}`,
  approvalAction: (approvalId: string, action: 'approve' | 'deny' | 'cancel') =>
    `${getApiUrl('approvals')}/api/approvals/${encodeURIComponent(approvalId)}/${action}`
};

// Health check endpoints for monitoring
export const HEALTH_ENDPOINTS = {
  aiImages: `${API_CONFIG.microservices.aiImages.url}/health`,
  menuManagement: `${API_CONFIG.microservices.menuManagement.url}/health`,
  taxManagement: `${API_CONFIG.microservices.taxManagement.url}/health`,
  coreData: `${API_CONFIG.microservices.coreData.url}/health`,
  stores: `${API_CONFIG.microservices.stores.url}/health`,
  dayparts: `${API_CONFIG.microservices.dayparts.url}/health`,
  employees: `${API_CONFIG.microservices.employees.url}/health`,
  socialMedia: `${API_CONFIG.microservices.socialMedia.url}/health`,
  discounts: `${API_CONFIG.microservices.discounts.url}/health`,
  securityAudit: `${API_CONFIG.microservices.securityAudit.url}/health`,
  contactForm: `${API_CONFIG.microservices.contactForm.url}/health`,
  ubiquitiNetwork: `${API_CONFIG.microservices.ubiquitiNetwork.url}/health`,
  messaging: `${API_CONFIG.microservices.messaging.url}/health`,
  categories: `${API_CONFIG.microservices.categories.url}/health`,
  menuBoards: `${API_CONFIG.microservices.menuBoards.url}/health`,
  scheduling: `${API_CONFIG.microservices.scheduling.url}/health`,
  posSync: `${API_CONFIG.microservices.posSync.url}/health`,
  tableCharts: `${API_CONFIG.microservices.tableCharts.url}/health`,
  weather: `${API_CONFIG.microservices.weather.url}/health`,
  waitlist: `${API_CONFIG.microservices.waitlist.url}/health`,
  payment: `${API_CONFIG.microservices.payment.url}/health`,
  aiChat: `${API_CONFIG.microservices.aiChat.url}/health`,
  voiceOrdering: `${API_CONFIG.microservices.voiceOrdering.url}/health`,
  voiceConfig: `${API_CONFIG.microservices.voiceConfig.url}/health`,
  phoneAssistant: `${API_CONFIG.microservices.phoneAssistant.url}/health`,
  equipmentMonitoring: `${API_CONFIG.microservices.equipmentMonitoring.url}/health`,
  posIntegration: `${API_CONFIG.microservices.posIntegration.url}/api/pos/health`,
  reviewManagement: `${API_CONFIG.microservices.reviewManagement.url}/api/reviews/health`,
  reviewTrigger: `${API_CONFIG.microservices.reviewTrigger.url}/api/review-trigger/health`,
  supportChat: `${API_CONFIG.microservices.supportChat.url}/health`,
  billing: `${API_CONFIG.microservices.billing.url}/api/billing/health`,
  stripeWebhooks: `${API_CONFIG.microservices.stripeWebhooks.url}/health`,
  commandCenter: `${API_CONFIG.microservices.commandCenter.url}/api/command-center/health`,
  suggestionEngine: `${API_CONFIG.microservices.suggestionEngine.url}/api/suggestions/health`,
  askBso: `${API_CONFIG.microservices.askBso.url}/api/ask-bso/health`,
  // Intelligent Inventory microservices
  inventoryCore: `${API_CONFIG.microservices.inventoryCore.url}/api/inventory/health`,
  recipes: `${API_CONFIG.microservices.recipes.url}/api/recipes/health`,
  inventoryForecast: `${API_CONFIG.microservices.inventoryForecast.url}/api/inventory-forecast/health`,
  inventoryOrders: `${API_CONFIG.microservices.inventoryOrders.url}/api/inventory-orders/health`,
  inventoryReceiving: `${API_CONFIG.microservices.inventoryReceiving.url}/api/inventory-receiving/health`,
  inventoryInvoices: `${API_CONFIG.microservices.inventoryInvoices.url}/api/inventory-invoices/health`,
  inventoryVision: `${API_CONFIG.microservices.inventoryVision.url}/api/inventory-vision/health`,
  suppliers: `${API_CONFIG.microservices.suppliers.url}/api/suppliers/health`,
  inventoryNetwork: `${API_CONFIG.microservices.inventoryNetwork.url}/api/inventory-network/health`,
  inventoryWaste: `${API_CONFIG.microservices.inventoryWaste.url}/api/inventory-waste/health`,
  uploads: `${API_CONFIG.microservices.uploads.url}/api/uploads/health`,
  pricing: `${API_CONFIG.microservices.pricing.url}/api/pricing/health`,
  modifiers: `${API_CONFIG.microservices.modifiers.url}/api/modifiers/health`,
  approvals: `${API_CONFIG.microservices.approvals.url}/api/approvals/health`
};

// Utility function to check API health
export const checkApiHealth = async (apiType: keyof typeof HEALTH_ENDPOINTS): Promise<boolean> => {
  try {
    // First try a proper health check
    const response = await fetch(HEALTH_ENDPOINTS[apiType], {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      return true;
    }
    
    // If health endpoint returns non-OK status, it's still responding
    if (response.status >= 400 && response.status < 600) {
      console.log(`${apiType} health endpoint returned status ${response.status}, but is responding`);
      return true; // API is up, just the health endpoint might not exist
    }
    
    return false;
  } catch (error) {
    // Check if it's a CORS error (which means the API is actually responding)
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    if (errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch')) {
      // For CORS errors, try a no-cors request to check if API is up
      try {
        await fetch(HEALTH_ENDPOINTS[apiType], {
          method: 'HEAD',
          mode: 'no-cors'
        });
        // If we get here without throwing, the API is responding
        console.log(`${apiType} is responding but has CORS restrictions on health endpoint`);
        return true;
      } catch (networkError) {
        // This is a real network error, API is down
        console.error(`${apiType} is not responding:`, networkError);
        return false;
      }
    }
    
    console.error(`Health check failed for ${apiType}:`, error);
    return false;
  }
};

// Microservice management utilities
export const enableMicroservice = (apiType: keyof typeof MICROSERVICES_ENABLED) => {
  MICROSERVICES_ENABLED[apiType] = true;
  console.log(`ENABLED: Microservice for ${apiType}`);
};

export const disableMicroservice = (apiType: keyof typeof MICROSERVICES_ENABLED) => {
  MICROSERVICES_ENABLED[apiType] = false;
  console.log(`WARNING: Disabled microservice for ${apiType}`);
};

// Dual-mode migration from legacy Lambda URLs to API Gateway is complete. The
// former BYPASS_API_GATEWAY list and traffic-splitting helpers were dead code
// once routing was consolidated behind the Gateway.

/**
 * Get API Gateway URL for a specific service
 */
const getGatewayUrl = (apiType: keyof typeof MICROSERVICES_ENABLED): string | null => {
  const { baseUrl } = API_CONFIG.gateway;

  // Map service types to API Gateway paths
  const servicePaths = {
    aiImages: '/api/images',
    menuManagement: '', // Menu endpoints are at root level (/api/menu-items)
    taxManagement: '/api/tax',
    coreData: '', // Core data endpoints are at root level (/api/enterprises, /api/concepts, etc)
    stores: '', // Stores endpoints are at root level (/api/stores)
    dayparts: '/api/dayparts',
    employees: '', // Employees endpoints are at root level (/api/employees/*) - path is in API_ENDPOINTS
    socialMedia: '', // Social media endpoints are at root level (/api/social-media/*)
    discounts: '', // Discount endpoints are at root level (/api/discounts/*)
    securityAudit: '', // Security audit endpoints include full path /api/security-audit
    contactForm: '/api/contact',
    ubiquitiNetwork: '/api/network',
    messaging: '/api/messaging',
    categories: '', // Categories endpoints are at root level (part of core data)
    menuBoards: '', // Menu Boards endpoints are at root level (/api/menu-boards)
    scheduling: '/api/scheduling',
    posSync: '/api/pos',
    tableCharts: '/api/tables',
    weather: '/api/weather',
    waitlist: '/api/waitlist', // API Gateway route prefix for waitlist
    payment: '/api/payment',
    aiChat: '/api/chat',
    voiceOrdering: '/api/voice', // Voice ordering session management
    voiceConfig: '/api/voice-config', // Voice configuration and device management
    phoneAssistant: '/api/phone-assistant', // Phone AI assistant (AWS Connect/Lex)
    equipmentMonitoring: '/api/equipment', // Restaurant equipment monitoring and reporting
    posIntegration: '/api/pos-integration', // POS integration (Toast adapter, order polling)
    reviewManagement: '', // Review management - full paths in API_ENDPOINTS (/api/reviews/*, /api/recovery/*)
    reviewTrigger: '', // Review trigger - full paths in API_ENDPOINTS (/api/review-trigger/*)
    supportChat: '/api/support', // Support chat conversations and escalation
    billing: '/api/billing', // Billing checkout and portal sessions
    stripeWebhooks: '/api/stripe', // Stripe webhook event processing
    commandCenter: '/api/command-center', // Command Center analytics dashboard
    suggestionEngine: '/api/suggestions', // AI suggestion engine
    askBso: '/api/ask-bso', // Conversational analytics
    // Intelligent Inventory microservices
    // Path prefixes below are '' because each endpoint builder below
    // includes the full `/api/x/...` path. Non-empty prefixes here would
    // double the prefix and give `/api/modifiers/api/modifiers/groups`.
    inventoryCore: '',
    recipes: '',
    inventoryForecast: '',
    inventoryOrders: '',
    inventoryReceiving: '',
    inventoryInvoices: '',
    inventoryVision: '',
    suppliers: '',
    inventoryNetwork: '',
    inventoryWaste: '',
    uploads: '',
    pricing: '',
    modifiers: '',
    approvals: ''
  };
  
  const path = servicePaths[apiType as keyof typeof servicePaths];
  if (path === undefined) return null;
  return path === '' ? baseUrl : `${baseUrl}${path}`;
};

// Global token getter (will be set by ApiGatewayInitializer)
let globalTokenGetter: (() => Promise<string | null>) | null = null;

export const setGlobalTokenGetter = (getter: () => Promise<string | null>) => {
  globalTokenGetter = getter;
};

/**
 * Enhanced fetch wrapper with automatic authentication and error handling
 */
export const apiRequest = async (
  apiType: keyof typeof MICROSERVICES_ENABLED,
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const baseUrl = getApiUrl(apiType);
  const url = `${baseUrl}${endpoint}`;

  let token: string | null = null;
  if (globalTokenGetter) {
    try {
      token = await globalTokenGetter();
    } catch {
      // Fall through to storage-based token below.
    }
  }
  if (!token) {
    token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken') ||
            sessionStorage.getItem('idToken') || localStorage.getItem('idToken');
  }

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, {
    ...options,
    headers: { ...defaultHeaders, ...options.headers }
  });
};

// Legacy placeholder — routing is gateway-only, so these retain their exports
// but no longer influence behavior.
export const getApiStatus = () => {
  return {
    configuration: API_CONFIG,
    microservicesEnabled: MICROSERVICES_ENABLED,
    currentUrls: {
      gateway: {
        aiImages: getGatewayUrl('aiImages'),
        menuManagement: getGatewayUrl('menuManagement'),
        coreData: getGatewayUrl('coreData'),
        stores: getGatewayUrl('stores')
      },
      active: {
        aiImages: getApiUrl('aiImages'),
        menuManagement: getApiUrl('menuManagement'),
        coreData: getApiUrl('coreData'),
        stores: getApiUrl('stores')
      }
    }
  };
};